// API Service - Connects Dashboard to Real Backend
// Path: dashboard/src/services/api.ts

import { getAuthToken } from '@/hooks/useAuth'
import { getCurrentTenantId } from '@/hooks/useTenant'
import { logAPI, logAPIError } from '@/utils/debugLogger'

// Remove trailing slash to avoid double slashes in URLs
export const API_BASE = (import.meta.env.VITE_API_URL || 'https://chbp18qo6g.execute-api.eu-north-1.amazonaws.com/dev').replace(/\/+$/, '')

// Get dynamic tenant ID from authenticated user
function getTenantId(): string {
  const tenantId = getCurrentTenantId()
  if (!tenantId) {
    const forced = import.meta.env.VITE_TENANT_ID
    if (import.meta.env.DEV && forced) {
      logAPI(`Using forced tenant_id from env: ${forced}`)
      return forced
    }
    logAPIError('Tenant not resolved', { currentTenantId: tenantId })
    throw new Error('[API] Tenant not resolved. Please login / set tenant context.')
  }
  return tenantId
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  body?: any
  headers?: Record<string, string>
}

// Friendly error messages for common API errors by endpoint pattern
const FRIENDLY_ERRORS: Array<{ pattern: RegExp; errors: Record<number, string> }> = [
  { pattern: /\/v1\/integrations/, errors: { 400: 'Invalid credentials. Please check and try again.', 401: 'Session expired. Please log in again.' } },
  { pattern: /\/v1\/chat/, errors: { 500: 'AI service temporarily unavailable. Please try again in a moment.', 503: 'AI service temporarily unavailable. Please try again in a moment.' } },
  { pattern: /\/v1\/knowledge-base\/upload/, errors: { 413: 'File too large. Maximum size is 10 MB.' } },
  { pattern: /\/v1\/knowledge-base/, errors: { 413: 'File too large. Maximum size is 10 MB.' } },
  { pattern: /\/v1\/tickets\/[^/]+$/, errors: { 404: 'Ticket not found.', 409: 'Ticket was modified by another user. Please refresh.' } },
  { pattern: /\/v1\/agents/, errors: { 404: 'Agent not found.', 409: 'Agent configuration conflict. Please refresh and try again.' } },
  { pattern: /\/v1\/router/, errors: { 500: 'AI Router service error. Please try again.' } },
]

// Generic status code messages when no endpoint-specific match
const GENERIC_STATUS_ERRORS: Record<number, string> = {
  400: 'Invalid request. Please check your input.',
  401: 'Session expired. Please log in again.',
  403: 'You don\'t have permission to perform this action.',
  404: 'The requested resource was not found.',
  409: 'Conflict. The resource was modified by another user.',
  413: 'Request too large.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'Server error. Please try again.',
  502: 'Service temporarily unavailable. Please try again.',
  503: 'Service temporarily unavailable. Please try again.',
}

function getFriendlyError(endpoint: string, status: number): string | null {
  for (const { pattern, errors } of FRIENDLY_ERRORS) {
    if (pattern.test(endpoint) && errors[status]) {
      return errors[status]
    }
  }
  return GENERIC_STATUS_ERRORS[status] || null
}

async function apiRequest<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const startTime = Date.now()
  const method = options.method || 'GET'
  const token = await getAuthToken()
  const tenantId = getTenantId()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Tenant-Id': tenantId,
    ...options.headers,
  }

  if (token && token !== 'demo-token') {
    headers['Authorization'] = `Bearer ${token}`
  }

  logAPI(`Request: ${method} ${endpoint}`, {
    tenantId,
    hasAuth: !!headers['Authorization'],
  })

  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method,
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
    })

    const duration = Date.now() - startTime

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }))
      logAPIError(`HTTP ${response.status}: ${method} ${endpoint}`, {
        status: response.status,
        error,
        duration: `${duration}ms`,
      })
      // Use server message if specific, otherwise use friendly mapping
      const serverMsg = error.message || error.error
      const friendlyMsg = getFriendlyError(endpoint, response.status)
      throw new Error(serverMsg || friendlyMsg || `API Error: ${response.status}`)
    }

    logAPI(`Response: ${method} ${endpoint}`, {
      status: response.status,
      duration: `${duration}ms`,
    })

    // Algunas rutas (DELETE/204) no devuelven body JSON
    if (response.status === 204) return {} as T

    return response.json() as Promise<T>
  } catch (err) {
    const duration = Date.now() - startTime

    // Detect network errors (offline, DNS failure, CORS, etc.)
    if (err instanceof TypeError && err.message === 'Failed to fetch') {
      logAPIError(`Network Error: ${method} ${endpoint}`, {
        error: 'Failed to fetch',
        duration: `${duration}ms`,
      })
      throw new Error('Network error. Please check your internet connection and try again.')
    }

    logAPIError(`Error: ${method} ${endpoint}`, {
      error: err instanceof Error ? err.message : String(err),
      duration: `${duration}ms`,
    })
    throw err
  }
}

// =============================================================================
// CHAT API
// =============================================================================

export interface ChatMessage {
  message: string
  session_id?: string
  customer_email?: string
  order_number?: string
  metadata?: Record<string, any>
}

export interface ChatResponse {
  message: string
  session_id: string
  tenant_id: string
  category: string
  language: string
  model: string
  usage: {
    prompt_tokens: number
    completion_tokens: number
    total_tokens: number
  }
  kb_sources: string[]
  latency_ms: number
  escalation_level: string | null
  compensation_flag: boolean | null
  order_number: string | null
  timestamp: string
  agent_name: string
}

export async function sendChatMessage(data: ChatMessage): Promise<ChatResponse> {
  return apiRequest<ChatResponse>('/v1/chat', {
    method: 'POST',
    body: data,
  })
}

// =============================================================================
// CONVERSATIONS API
// =============================================================================

export interface Conversation {
  session_id: string
  tenant_id: string
  customer_email?: string
  status: 'active' | 'resolved' | 'escalated'
  category: string
  language: string
  created_at: string
  updated_at: string
  message_count: number
  last_message?: string
  escalation_level?: string
}

export interface ConversationMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  metadata?: Record<string, any>
}

export async function getConversations(params?: {
  status?: string
  limit?: number
  cursor?: string
}): Promise<{ conversations: Conversation[], next_cursor?: string }> {
  const queryParams = new URLSearchParams()
  if (params?.status) queryParams.append('status', params.status)
  if (params?.limit) queryParams.append('limit', params.limit.toString())
  if (params?.cursor) queryParams.append('cursor', params.cursor)
  
  const query = queryParams.toString()
  return apiRequest(`/v1/conversations${query ? `?${query}` : ''}`)
}

export async function getConversation(sessionId: string): Promise<{
  conversation: Conversation
  messages: ConversationMessage[]
}> {
  return apiRequest(`/v1/conversations/${sessionId}`)
}

export async function updateConversationStatus(
  sessionId: string, 
  status: 'resolved' | 'escalated'
): Promise<Conversation> {
  return apiRequest(`/v1/conversations/${sessionId}`, {
    method: 'PATCH',
    body: { status },
  })
}

// =============================================================================
// TICKETS API
// =============================================================================

export interface TicketMetrics {
  first_response_time_minutes?: number | null
  resolution_time_minutes?: number | null
  total_response_time_minutes?: number | null
  sla_due_at?: string | null
  resolution_due_at?: string | null
  sla_breached?: boolean
  sla_first_response_breached?: boolean
}

export interface Ticket {
  ticket_id: string
  tenant_id: string
  thread_id?: string
  session_id?: string
  status: 'open' | 'pending' | 'resolved' | 'closed'
  priority: 'low' | 'normal' | 'high' | 'urgent'
  assigned_to?: string
  assigned_agent_name?: string
  category?: string
  customer_email: string
  customer_name?: string
  subject: string
  first_message?: string
  last_message?: string
  preview?: string
  channel: 'email' | 'chat' | 'phone' | 'sms' | 'whatsapp' | 'web_form'
  tags: string[]
  ai_summary?: string
  sentiment?: 'positive' | 'neutral' | 'negative'
  message_count: number
  response_count?: number
  snoozed_until?: string | null
  first_response_at?: string | null
  resolved_at?: string | null
  metrics?: TicketMetrics | null
  created_at: string
  updated_at: string
  requires_human_review?: boolean
}

export interface TicketAttachment {
  attachment_id: string
  filename: string
  content_type: string
  size_bytes: number
  url?: string
  s3_key?: string
}

export interface TicketMessage {
  message_id: string
  ticket_id: string
  tenant_id?: string
  role: 'customer' | 'agent' | 'system'
  author_name: string
  content: string
  html_content?: string
  type: 'email' | 'note' | 'status_change' | 'assignment' | 'tag_change'
  email_id?: string
  draft_id?: string
  is_draft?: boolean
  is_sent?: boolean
  attachments?: TicketAttachment[]
  created_at: string
  sent_at?: string
}

export interface TicketDetail {
  ticket: Ticket
  messages: TicketMessage[]
  customer_info?: Record<string, unknown>
}

export async function getTickets(params?: {
  status?: string
  priority?: string
  tags?: string
  channel?: string
  search?: string
  page?: number
  per_page?: number
  sort_by?: string
  sort_order?: string
  cursor?: string
}): Promise<{ tickets: Ticket[]; total: number; page: number; per_page: number; has_more: boolean; next_cursor?: string }> {
  const queryParams = new URLSearchParams()
  if (params?.status) queryParams.append('status', params.status)
  if (params?.priority) queryParams.append('priority', params.priority)
  if (params?.tags) queryParams.append('tags', params.tags)
  if (params?.channel) queryParams.append('channel', params.channel)
  if (params?.search) queryParams.append('search', params.search)
  if (params?.page) queryParams.append('page', params.page.toString())
  if (params?.per_page) queryParams.append('per_page', params.per_page.toString())
  if (params?.sort_by) queryParams.append('sort_by', params.sort_by)
  if (params?.sort_order) queryParams.append('sort_order', params.sort_order)
  if (params?.cursor) queryParams.append('cursor', params.cursor)

  const query = queryParams.toString()
  return apiRequest(`/v1/tickets${query ? `?${query}` : ''}`)
}

export async function getTicketDetail(ticketId: string): Promise<TicketDetail> {
  return apiRequest(`/v1/tickets/${ticketId}`)
}

export async function createTicket(data: {
  customer_email: string
  customer_name?: string
  subject: string
  message: string
  channel?: string
  priority?: string
  tags?: string[]
  thread_id?: string
}): Promise<Ticket> {
  return apiRequest('/v1/tickets', {
    method: 'POST',
    body: data,
  })
}

export async function updateTicket(ticketId: string, data: Partial<Ticket>): Promise<Ticket> {
  return apiRequest(`/v1/tickets/${ticketId}`, {
    method: 'PUT',
    body: data,
  })
}

export async function updateTicketStatus(ticketId: string, status: string): Promise<Ticket> {
  return apiRequest(`/v1/tickets/${ticketId}`, {
    method: 'PUT',
    body: { action: 'update_status', status },
  })
}

export async function snoozeTicket(ticketId: string, snoozeUntil: string): Promise<Ticket> {
  return apiRequest(`/v1/tickets/${ticketId}`, {
    method: 'PUT',
    body: { action: 'snooze', snooze_until: snoozeUntil },
  })
}

export async function unsnoozeTicket(ticketId: string): Promise<Ticket> {
  return apiRequest(`/v1/tickets/${ticketId}`, {
    method: 'PUT',
    body: { action: 'snooze', snooze_until: null },
  })
}

export async function assignTicket(ticketId: string, assignedTo: string, assignedAgentName?: string): Promise<Ticket> {
  return apiRequest(`/v1/tickets/${ticketId}`, {
    method: 'PUT',
    body: { action: 'assign', assigned_to: assignedTo, assigned_agent_name: assignedAgentName },
  })
}

export async function updateTicketTags(ticketId: string, tags: string[]): Promise<Ticket> {
  return apiRequest(`/v1/tickets/${ticketId}`, {
    method: 'PUT',
    body: { action: 'update_tags', tags },
  })
}

export async function addTicketMessage(ticketId: string, data: {
  content: string
  role?: 'agent' | 'customer' | 'system'
  author_name: string
  type?: 'email' | 'note'
  is_draft?: boolean
  attachments?: TicketAttachment[]
}): Promise<TicketMessage> {
  return apiRequest(`/v1/tickets/${ticketId}`, {
    method: 'PUT',
    body: { action: 'add_message', ...data },
  })
}

export async function uploadTicketAttachment(ticketId: string, file: File): Promise<TicketAttachment> {
  const formData = new FormData()
  formData.append('file', file)

  const tenantId = getTenantId()
  const token = await getAuthToken()

  const headers: Record<string, string> = {
    'X-Tenant-Id': tenantId,
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const resp = await fetch(`${API_BASE}/v1/tickets/${ticketId}/attachments`, {
    method: 'POST',
    headers,
    body: formData,
  })

  if (!resp.ok) {
    const error = await resp.json().catch(() => ({ error: 'Upload failed' }))
    throw new Error(error.error || `Upload failed: ${resp.status}`)
  }

  return resp.json()
}

export async function mergeTickets(primaryTicketId: string, secondaryTicketIds: string[]): Promise<{
  primary_ticket_id: string
  merged_count: number
  messages_moved: number
}> {
  return apiRequest('/v1/tickets', {
    method: 'POST',
    body: { action: 'merge', primary_ticket_id: primaryTicketId, secondary_ticket_ids: secondaryTicketIds },
  })
}

export async function backfillTickets(): Promise<{ created: number; skipped: number; errors: string[]; total_sessions: number }> {
  return apiRequest('/v1/tickets', {
    method: 'POST',
    body: { action: 'backfill' },
  })
}

export async function getTicketsCount(since?: string): Promise<{ count: number; since: string | null }> {
  const queryParams = new URLSearchParams({ count_only: 'true' })
  if (since) queryParams.append('since', since)
  return apiRequest(`/v1/tickets?${queryParams.toString()}`)
}

// =============================================================================
// TICKET VIEWER TRACKING (collision detection)
// =============================================================================

export async function registerTicketViewer(ticketId: string, userEmail: string, userName: string): Promise<void> {
  await apiRequest(`/v1/tickets/${ticketId}`, {
    method: 'PUT',
    body: { action: 'view_start', user_email: userEmail, user_name: userName },
  })
}

export async function unregisterTicketViewer(ticketId: string, userEmail: string): Promise<void> {
  await apiRequest(`/v1/tickets/${ticketId}`, {
    method: 'PUT',
    body: { action: 'view_end', user_email: userEmail },
  })
}

export interface TicketViewer {
  user_email: string
  user_name: string
  started_at: string
}

export async function getTicketViewers(ticketId: string): Promise<{ viewers: TicketViewer[]; ticket_id: string }> {
  return apiRequest(`/v1/tickets/${ticketId}/viewers`)
}

// =============================================================================
// MACROS API
// =============================================================================

export interface Macro {
  macro_id: string
  tenant_id?: string
  name: string
  content: string
  category: string
  variables: string[]
  is_active?: boolean
  created_at?: string
  updated_at?: string
}

export async function getMacros(): Promise<{ macros: Macro[]; total: number }> {
  return apiRequest('/v1/macros')
}

export async function createMacro(data: {
  name: string
  content: string
  category?: string
  variables?: string[]
}): Promise<{ macro: Macro; message: string }> {
  return apiRequest('/v1/macros', { method: 'POST', body: data })
}

export async function updateMacro(macroId: string, data: Partial<Macro>): Promise<{ macro: Macro; message: string }> {
  return apiRequest(`/v1/macros/${macroId}`, { method: 'PUT', body: data })
}

export async function deleteMacro(macroId: string): Promise<{ message: string }> {
  return apiRequest(`/v1/macros/${macroId}`, { method: 'DELETE' })
}

// =============================================================================
// TAGS API
// =============================================================================

export interface Tag {
  tag_name: string
  tag_category: 'motivo' | 'estado' | 'prioridad' | 'canal' | 'especial' | 'custom'
  added_by: string
  added_at: string
}

export interface TagTaxonomy {
  taxonomy: Record<string, string[]>
  categories: string[]
}

export async function addTagToTicket(ticketId: string, tagName: string, tagCategory: string): Promise<{ message: string; tag: Tag }> {
  return apiRequest(`/v1/tickets/${ticketId}/tags`, {
    method: 'POST',
    body: { tag_name: tagName, tag_category: tagCategory },
  })
}

export async function removeTagFromTicket(ticketId: string, tagName: string): Promise<{ message: string }> {
  return apiRequest(`/v1/tickets/${ticketId}/tags/${tagName}`, {
    method: 'DELETE',
  })
}

export async function getTicketTags(ticketId: string): Promise<{ tags: Tag[] }> {
  return apiRequest(`/v1/tickets/${ticketId}/tags`)
}

export async function getTagTaxonomy(): Promise<TagTaxonomy> {
  return apiRequest('/v1/tags/taxonomy')
}

// =============================================================================
// ANALYTICS API
// =============================================================================

export interface AnalyticsData {
  period: string
  total_conversations: number
  total_messages: number
  avg_response_time_ms: number
  resolution_rate: number
  escalation_rate: number
  categories: Record<string, number>
  languages: Record<string, number>
  hourly_distribution: number[]
  daily_trend: { date: string; count: number }[]
  top_intents: { intent: string; count: number }[]
  satisfaction_score?: number
}

export interface AgentMetric {
  agent_id: string
  agent_name: string
  tickets_total: number
  tickets_resolved: number
  tickets_open: number
  avg_first_response_minutes: number | null
  avg_resolution_minutes: number | null
  sla_compliance_percent: number | null
}

export interface AgentMetricsData {
  type: 'agent_metrics'
  total_tickets: number
  agents: AgentMetric[]
}

export async function getAgentMetrics(): Promise<AgentMetricsData> {
  return apiRequest('/v1/analytics?type=agents')
}

export async function getAnalytics(params?: {
  start_date?: string
  end_date?: string
  period?: 'day' | 'week' | 'month'
}): Promise<AnalyticsData> {
  const queryParams = new URLSearchParams()
  if (params?.start_date) queryParams.append('start_date', params.start_date)
  if (params?.end_date) queryParams.append('end_date', params.end_date)
  if (params?.period) queryParams.append('period', params.period)
  
  const query = queryParams.toString()
  return apiRequest(`/v1/analytics${query ? `?${query}` : ''}`)
}

// =============================================================================
// TENANT API
// =============================================================================

export interface TenantConfig {
  tenant_id: string
  name: string
  agent_name: string
  default_language: string
  welcome_message: string
  business_hours?: {
    timezone: string
    schedule: Record<string, { open: string; close: string }>
  }
  features: {
    shopify_enabled: boolean
    gmail_enabled: boolean
    knowledge_base_enabled: boolean
  }
  widget_config: {
    primary_color: string
    position: 'bottom-right' | 'bottom-left'
    greeting: string
  }
  created_at: string
  updated_at: string
}

export async function getTenantConfig(): Promise<TenantConfig> {
  const tenantId = getTenantId()
  return apiRequest(`/v1/tenants/${tenantId}`)
}

export async function updateTenantConfig(data: Partial<TenantConfig>): Promise<TenantConfig> {
  const tenantId = getTenantId()
  return apiRequest(`/v1/tenants/${tenantId}`, {
    method: 'PUT',
    body: data,
  })
}

// =============================================================================
// AGENTS API
// =============================================================================

export type AgentCategory = string

export interface ToneOfVoiceConfig {
  style?: string
  use_emojis?: boolean
  allowed_emojis?: string[]
  max_emojis_per_response?: number
  formatting_style?: string
  use_dashes?: boolean
  use_bullet_points?: boolean
  max_bullet_points?: number
  max_sentences?: number
  prefer_short_responses?: boolean
  use_contractions?: boolean
  use_exclamations?: boolean
  formality_level?: number
  greeting_style?: string
  closing_style?: string
  use_customer_name?: boolean
  [key: string]: any
}

export interface AgentRule {
  rule_id: string
  text: string
  is_do: boolean
  priority: number
  is_active: boolean
  [key: string]: any
}

export interface ResponseTemplate {
  template_id: string
  name: string
  content: string
  category: string
  language?: string
  description?: string
  variables?: string[]
  is_active?: boolean
}

export interface CustomAgent {
  agent_id: string
  tenant_id?: string

  name: string
  description?: string

  categories: AgentCategory[]
  rules: AgentRule[]

  global_prompt?: string
  agent_prompt?: string
  common_tone?: string
  escalation_protocol?: string
  compensation_limits?: string
  corporate_guardrails?: string
  immutable_rules?: string
  identity_and_format?: string
  operational_protocols?: string
  master_base?: string
  knowledge_base_file_ids?: string[]
  store_data?: string
  context_instructions?: string
  special_instructions?: string

  is_default?: boolean
  is_active: boolean
  use_global_settings?: boolean  // Whether to apply Global Agent Settings to this agent
  router_enabled?: boolean  // Whether this agent is used by AI Router for email classification
  draft_response_enabled?: boolean  // Whether this agent generates AI draft responses
  response_mode?: string  // "auto_reply", "template", or "manual"
  response_templates?: ResponseTemplate[]
  version?: number

  tone?: ToneOfVoiceConfig

  temperature?: number
  max_tokens?: number
  default_language?: string
  supported_languages?: string[]
  signature_name?: string
  signature_company?: string

  escalation_enabled?: boolean
  max_auto_compensation?: number
  max_auto_discount_percent?: number
  enabled_tools?: string[]

  created_at?: string
  updated_at?: string

  [key: string]: any
}

export interface GetAgentsResponse {
  agents: CustomAgent[]
  categories: AgentCategory[]
}

export async function getAgents(): Promise<GetAgentsResponse> {
  return apiRequest<GetAgentsResponse>('/v1/agents', { method: 'GET' })
}

export async function createAgent(
  data: Partial<CustomAgent>
): Promise<{ agent: CustomAgent }> {
  return apiRequest<{ agent: CustomAgent }>('/v1/agents', {
    method: 'POST',
    body: data,
  })
}

export async function updateAgent(
  agentId: string,
  data: Partial<CustomAgent>
): Promise<{ agent: CustomAgent }> {
  return apiRequest<{ agent: CustomAgent }>(`/v1/agents/${agentId}`, {
    method: 'PUT',
    body: data,
  })
}

export async function deleteAgent(agentId: string): Promise<{ success: boolean }> {
  return apiRequest<{ success: boolean }>(`/v1/agents/${agentId}`, {
    method: 'DELETE',
  })
}

export async function createDefaultAgents(
  companyName: string
): Promise<{ agents: CustomAgent[] }> {
  return apiRequest<{ agents: CustomAgent[] }>('/v1/agents/default-agents', {
    method: 'POST',
    body: { company_name: companyName },
  })
}

export async function addAgentRule(
  agentId: string,
  rule: Omit<AgentRule, 'rule_id'> & Partial<Pick<AgentRule, 'rule_id'>>
): Promise<{ agent: CustomAgent }> {
  return apiRequest<{ agent: CustomAgent }>(`/v1/agents/${agentId}/rules`, {
    method: 'POST',
    body: rule,
  })
}

export async function removeAgentRule(
  agentId: string,
  ruleId: string
): Promise<{ agent: CustomAgent }> {
  return apiRequest<{ agent: CustomAgent }>(`/v1/agents/${agentId}/rules/${ruleId}`, {
    method: 'DELETE',
  })
}

// =============================================================================
// GLOBAL AGENT CONFIG API
// =============================================================================

export interface GlobalAgentConfig {
  model?: string
  reasoning_effort?: string
  common_tone?: string
  escalation_protocol?: string
  compensation_limits?: string
  corporate_guardrails?: string
  store_data?: string
  context_instructions?: string
  special_instructions?: string
  ai_draft_label?: string  // Gmail label for AI-generated drafts
  created_by?: string
  updated_by?: string
  created_at?: string
  updated_at?: string
}

export async function getGlobalAgentConfig(): Promise<GlobalAgentConfig> {
  return apiRequest<GlobalAgentConfig>('/v1/agents/global-config', {
    method: 'GET',
  })
}

export async function updateGlobalAgentConfig(
  config: Partial<GlobalAgentConfig>
): Promise<{ config: GlobalAgentConfig; message: string }> {
  return apiRequest<{ config: GlobalAgentConfig; message: string }>('/v1/agents/global-config', {
    method: 'PUT',
    body: config,
  })
}

// =============================================================================
// ROUTER API
// =============================================================================

export interface RouterConfig {
  enabled: boolean
  model: string  // OpenAI: gpt-5-nano/mini/gpt-5/5.1/5.2 | Anthropic: claude-sonnet-4/opus-4 | Google: gemini-2.5-flash/pro | xAI: grok-3/3-mini
  reasoning_effort: string  // low, medium, high (supported by GPT-5 models)
  system_prompt?: string
  fallback_agent_id?: string
  // Auto-classification settings
  auto_classify_enabled?: boolean  // Enable automatic classification of new emails
  auto_classify_since?: string  // ISO datetime - only classify emails after this date
  // AI Priority classification
  auto_priority_enabled?: boolean  // Enable automatic priority classification (low/normal/high/urgent)
  created_by?: string
  updated_by?: string
  created_at?: string
  updated_at?: string
}

export async function getRouterConfig(): Promise<RouterConfig> {
  return apiRequest<RouterConfig>('/v1/router/config', {
    method: 'GET',
  })
}

export async function updateRouterConfig(
  config: Partial<RouterConfig>
): Promise<{ config: RouterConfig; message: string }> {
  return apiRequest<{ config: RouterConfig; message: string }>('/v1/router/config', {
    method: 'PUT',
    body: config,
  })
}

// =============================================================================
// BULK RE-CLASSIFY API
// =============================================================================

export interface BulkReclassifyParams {
  since_date?: string
  force?: boolean
  include_priority?: boolean
}

export interface BulkReclassifyPriorityParams {
  since_date?: string
  force?: boolean
}

export interface BulkReclassifyResult {
  total_found: number
  reclassified: number
  skipped: number
  errors: number
  error_details: string[]
}

export interface BulkReclassifyStarted {
  status: string
  message: string
}

export interface BulkReclassifyProgress {
  status: 'idle' | 'started' | 'processing' | 'completed' | 'error'
  total_found: number
  reclassified: number
  skipped: number
  errors: number
  error_details: string[]
  message: string
  updated_at: string
}

export async function bulkReclassify(
  params: BulkReclassifyParams
): Promise<BulkReclassifyStarted> {
  return apiRequest<BulkReclassifyStarted>('/v1/router/bulk-reclassify', {
    method: 'POST',
    body: params,
  })
}

export async function bulkReclassifyPriority(
  params: BulkReclassifyPriorityParams
): Promise<BulkReclassifyStarted> {
  return apiRequest<BulkReclassifyStarted>('/v1/router/bulk-reclassify-priority', {
    method: 'POST',
    body: params,
  })
}

export async function getBulkReclassifyProgress(): Promise<BulkReclassifyProgress> {
  return apiRequest<BulkReclassifyProgress>('/v1/router/auto-draft-progress?type=bulk-reclassify')
}

export async function getBulkPriorityProgress(): Promise<BulkReclassifyProgress> {
  return apiRequest<BulkReclassifyProgress>('/v1/router/auto-draft-progress?type=bulk-priority')
}

// =============================================================================
// PRIORITY CLASSIFIER API
// =============================================================================

export interface PriorityConfig {
  enabled: boolean
  model: string
  reasoning_effort: string
  system_prompt?: string | null
  default_prompt?: string
}

export async function getPriorityConfig(): Promise<PriorityConfig> {
  return apiRequest<PriorityConfig>('/v1/router/priority-config', {
    method: 'GET',
  })
}

export async function updatePriorityConfig(
  config: Partial<PriorityConfig>
): Promise<{ config: PriorityConfig; message: string }> {
  return apiRequest<{ config: PriorityConfig; message: string }>('/v1/router/priority-config', {
    method: 'PUT',
    body: config,
  })
}

// =============================================================================
// AI TRANSLATOR API
// =============================================================================

export interface TranslateConfig {
  enabled: boolean
  model: string
  reasoning_effort: string
  target_language: string
  system_prompt?: string | null
  default_prompt?: string
}

export async function getTranslateConfig(): Promise<TranslateConfig> {
  return apiRequest<TranslateConfig>('/v1/router/translate-config', {
    method: 'GET',
  })
}

export async function updateTranslateConfig(
  config: Partial<TranslateConfig>
): Promise<{ config: TranslateConfig; message: string }> {
  return apiRequest<{ config: TranslateConfig; message: string }>('/v1/router/translate-config', {
    method: 'PUT',
    body: config,
  })
}

export async function translateText(
  text: string,
  options?: { source_language?: string; target_language?: string },
): Promise<{ translated_text: string }> {
  return apiRequest<{ translated_text: string }>('/v1/router/translate', {
    method: 'POST',
    body: { text, ...options },
  })
}

// =============================================================================
// USER PREFERENCES API
// =============================================================================

export interface UserPreferences {
  translate_from?: string
  translate_to?: string
  [key: string]: any
}

export async function getUserPreferences(): Promise<UserPreferences> {
  return apiRequest<UserPreferences>('/v1/router/user-preferences')
}

export async function updateUserPreferences(prefs: Partial<UserPreferences>): Promise<UserPreferences> {
  return apiRequest<UserPreferences>('/v1/router/user-preferences', {
    method: 'PUT',
    body: prefs,
  })
}

// =============================================================================
// KNOWLEDGE BASE API
// =============================================================================

export async function checkKBIntegration(): Promise<{
  success: boolean
  openai_connected: boolean
  message: string
}> {
  return apiRequest('/v1/knowledge-base/check-integration')
}

export interface KBDocument {
  document_id: string
  name: string
  description: string
  file_type: string
  category: string
  status: string
  size_bytes: number
  created_at: string
}

export interface VectorStore {
  id: string
  name: string
  status: string
  file_counts: {
    completed: number
    in_progress: number
    failed: number
  }
  created_at: number
}

export interface VSFile {
  id: string
  status: string
  filename: string
  purpose: string
  bytes: number
  created_at: number
}

export async function uploadKBFile(file: File): Promise<{
  success: boolean
  file_id: string
  filename: string
  size: number
}> {
  const token = await getAuthToken()
  const tenantId = getTenantId()

  const formData = new FormData()
  formData.append('file', file)

  const headers: Record<string, string> = {
    'X-Tenant-Id': tenantId,
  }
  if (token && token !== 'demo-token') {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE}/v1/knowledge-base/upload`, {
    method: 'POST',
    headers,
    body: formData,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Upload failed' }))
    throw new Error(error.error || `Upload failed: ${response.status}`)
  }

  return response.json()
}

export async function listVectorStores(): Promise<{ success: boolean; vector_stores: VectorStore[] }> {
  return apiRequest('/v1/knowledge-base/vector-stores')
}

export async function createVectorStore(name: string, fileIds: string[]): Promise<{
  success: boolean
  vector_store_id: string
  batch_id: string
  message: string
}> {
  return apiRequest('/v1/knowledge-base/vector-stores', {
    method: 'POST',
    body: { name, file_ids: fileIds },
  })
}

export async function listVSFiles(vsId: string): Promise<{ success: boolean; files: VSFile[] }> {
  return apiRequest(`/v1/knowledge-base/vector-stores/${vsId}/files`)
}

export async function addFilesToVS(vsId: string, fileIds: string[]): Promise<{
  success: boolean
  batch_id: string
  vector_store_id: string
  message: string
}> {
  return apiRequest(`/v1/knowledge-base/vector-stores/${vsId}/files`, {
    method: 'POST',
    body: { file_ids: fileIds },
  })
}

export async function deleteVSFile(vsId: string, fileId: string): Promise<{ success: boolean }> {
  return apiRequest(`/v1/knowledge-base/vector-stores/${vsId}/files/${fileId}`, {
    method: 'DELETE',
  })
}

export async function getVSFileContent(vsId: string, fileId: string): Promise<{
  success: boolean
  filename: string
  file_id: string
  content: string
  size: number
  message?: string
}> {
  return apiRequest(`/v1/knowledge-base/vector-stores/${vsId}/files/${fileId}/content`)
}

export async function getBatchStatus(vsId: string, batchId: string): Promise<{
  success: boolean
  batch_id: string
  status: string
  file_counts: {
    completed: number
    in_progress: number
    failed: number
    cancelled: number
    total: number
  }
  is_complete: boolean
}> {
  return apiRequest(`/v1/knowledge-base/vector-stores/${vsId}/batch/${batchId}/status`)
}

export async function getKBDocuments(): Promise<{ documents: KBDocument[] }> {
  return apiRequest('/v1/knowledge-base/documents')
}

export async function getKBDocumentContent(documentId: string): Promise<{
  success: boolean
  filename: string
  file_id: string
  content: string
  size: number
}> {
  return apiRequest(`/v1/knowledge-base/documents/${documentId}/content`)
}

export async function uploadKBDocument(data: {
  name: string
  description?: string
  category?: string
  file_base64?: string
  content?: string
  file_type?: string
}): Promise<{ success: boolean; document_id: string }> {
  return apiRequest('/v1/knowledge-base/documents', {
    method: 'POST',
    body: data,
  })
}

export async function deleteKBDocument(documentId: string): Promise<{ success: boolean }> {
  return apiRequest(`/v1/knowledge-base/documents/${documentId}`, {
    method: 'DELETE',
  })
}

export async function getKBStats(): Promise<{
  total_documents: number
  active_documents: number
  total_size_bytes: number
  total_size_mb: number
  by_category: Record<string, number>
  by_file_type: Record<string, number>
}> {
  return apiRequest('/v1/knowledge-base/stats')
}

export async function getKBCategories(): Promise<{
  categories: Array<{ value: string; label: string; description: string }>
}> {
  return apiRequest('/v1/knowledge-base/categories')
}

// =============================================================================
// SHOPIFY CUSTOMER PROFILE API
// =============================================================================

export interface ShopifyCustomerProfile {
  found: boolean
  is_new_customer?: boolean
  error?: string
  customer?: {
    shopify_id: number
    name: string
    email: string
    phone: string | null
    created_at: string
    orders_count: number
    total_spent: string
    currency: string
    is_vip: boolean
    tags: string[]
    default_address: {
      address1?: string
      address2?: string
      city?: string
      province?: string
      zip?: string
      country?: string
      phone?: string
    } | null
    accepts_marketing: boolean
    note?: string | null
  }
  recent_orders?: Array<{
    order_number: string
    admin_url?: string
    created_at: string
    total: string
    subtotal?: string
    shipping_cost?: string
    fulfillment_status: string
    financial_status: string
    items_count: number
    items: string[]
    items_detail?: Array<{
      title: string
      variant_title?: string
      quantity: number
      price: string
      sku?: string
    }>
    shipping_method?: string
    payment_method?: string
    discount?: {
      code: string
      amount: string
      type: string
    } | null
  }>
}

export async function getShopifyCustomerProfile(email: string): Promise<ShopifyCustomerProfile> {
  return apiRequest(`/v1/router/customer-profile?email=${encodeURIComponent(email)}`)
}

// =============================================================================
// HEALTH CHECK
// =============================================================================

export async function healthCheck(): Promise<{ status: string; timestamp: string }> {
  return apiRequest('/health')
}

// =============================================================================
// EXPORT DEFAULT API OBJECT
// =============================================================================

export const api = {
  chat: { send: sendChatMessage },
  conversations: { list: getConversations, get: getConversation, updateStatus: updateConversationStatus },
  tickets: {
    list: getTickets,
    get: getTicketDetail,
    create: createTicket,
    update: updateTicket,
    updateStatus: updateTicketStatus,
    snooze: snoozeTicket,
    unsnooze: unsnoozeTicket,
    assign: assignTicket,
    updateTags: updateTicketTags,
    addMessage: addTicketMessage,
    merge: mergeTickets,
    backfill: backfillTickets,
    count: getTicketsCount,
  },
  macros: { list: getMacros, create: createMacro, update: updateMacro, delete: deleteMacro },
  analytics: { get: getAnalytics, getAgentMetrics: getAgentMetrics },
  tenant: { getConfig: getTenantConfig, updateConfig: updateTenantConfig },
  agents: {
    list: getAgents,
    create: createAgent,
    update: updateAgent,
    delete: deleteAgent,
    createDefaults: createDefaultAgents,
    addRule: addAgentRule,
    removeRule: removeAgentRule,
    getGlobalConfig: getGlobalAgentConfig,
    updateGlobalConfig: updateGlobalAgentConfig,
  },
  knowledgeBase: {
    checkIntegration: checkKBIntegration,
    uploadFile: uploadKBFile,
    listVectorStores: listVectorStores,
    createVectorStore: createVectorStore,
    listVSFiles: listVSFiles,
    addFilesToVS: addFilesToVS,
    deleteVSFile: deleteVSFile,
    getVSFileContent: getVSFileContent,
    getBatchStatus: getBatchStatus,
    getDocuments: getKBDocuments,
    getDocumentContent: getKBDocumentContent,
    uploadDocument: uploadKBDocument,
    deleteDocument: deleteKBDocument,
    getStats: getKBStats,
    getCategories: getKBCategories,
  },
  health: healthCheck,
}

export default api

// =============================================================================
// ADMIN API — Super Admin tenant & user management
// =============================================================================

export interface TenantSummary {
  tenant_id: string
  name: string
  domain?: string
  plan: string
  status: string
  admin_email: string
  user_roles: Record<string, string>
  additional_admins: string[]
  usage: { messages_used: number; conversations_used: number }
  created_at: string
  updated_at: string
}

export interface TenantUser {
  email: string
  role: string
  status?: 'active' | 'invited'
}

export async function listAllTenants(): Promise<{ tenants: TenantSummary[]; count: number }> {
  return apiRequest('/v1/tenants')
}

export async function createTenant(data: {
  tenant_id: string
  name: string
  admin_email: string
  plan?: string
  domain?: string
}): Promise<{ message: string; tenant: TenantSummary }> {
  return apiRequest('/v1/tenants', { method: 'POST', body: data })
}

export async function listTenantUsers(tenantId: string): Promise<{ users: TenantUser[]; count: number }> {
  return apiRequest(`/v1/tenants/${tenantId}/users`)
}

export async function setTenantUserRole(tenantId: string, email: string, role: string): Promise<{ message: string }> {
  return apiRequest(`/v1/tenants/${tenantId}/users`, { method: 'PUT', body: { email, role } })
}

export async function removeTenantUser(tenantId: string, email: string): Promise<{ message: string }> {
  return apiRequest(`/v1/tenants/${tenantId}/users/${encodeURIComponent(email)}`, { method: 'DELETE' })
}

export async function inviteTeamMember(
  tenantId: string,
  email: string,
  role: string
): Promise<{ message: string; status: string }> {
  return apiRequest(`/v1/tenants/${tenantId}/users`, {
    method: 'PUT',
    body: { email, role, status: 'invited' },
  })
}

export async function updateTenantById(
  tenantId: string,
  data: Partial<TenantSummary>
): Promise<{ message: string; tenant: TenantSummary }> {
  return apiRequest(`/v1/tenants/${tenantId}`, { method: 'PUT', body: data })
}
